import {
  Table,
  Switch,
  Button,
  Tooltip,
  Space,
  Divider,
  Badge,
  Popover,
  Modal,
  Input,
  Typography,
} from "antd";
import { SettingOutlined, PictureOutlined, InfoCircleOutlined, ReloadOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import FallbackImage from "../../../components/FallbackImage";
import { useState } from "react";
import { selectUser } from "../../../redux/slices/user";
import { getColumnSearchProps } from "../../../utils/tables";
import Header from "../../../components/HeaderList";
import styles from "../styles/ProductsUI.module.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PriceEditor from "./PriceEditor";
import ProductDrawer from "./ProductDrawer";
import ConfigFilterDropdown from "../../../components/ConfigFilterDropdown";

const { Text } = Typography;

const FALLBACK_PHOTO = <PictureOutlined className={styles.placeholderPhoto} />;

const ProductsAdminUI = (props) => {
  const {
    loading,
    products,
    categories,
    confirmVisibility,
    visibleEditor,
    closeEditor,
    syncModalVisible,
    setSyncModalVisible,
    isSyncing,
    lastSyncTime,
    lastSyncStatus,
    syncOptions,
    setSyncOptions,
    handleSyncSubmit,
    handleToggleIgnoreOverrides,
    getProductMenu,
    getProductSteps,
    updateProductStepsOrder,
    initialFetch
  } = props;

  const user = useSelector(selectUser);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleOpenDrawer = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const getCategoryName = (role) => {
    const index = categories.findIndex((el) => el.value === role);
    if (index > -1) {
      return categories[index].text;
    }
    return "-";
  };

  const irhacia = useNavigate();
  const goToCondiments = () => {
    irhacia("/productos/condiments");
  };
  const goToSetmenus = () => {
    irhacia("/productos/combos");
  };
  const goToSuggets = () => {
    irhacia("/productos/recomendados");
  };

  const gotoProdSuc = () => {
    irhacia("/productos-por-sucursal");
  }

  // Extract unique channels dynamically from the product list to build filters
  const uniqueChannels = Array.from(
    new Set(
      (products || []).flatMap(p => p.channels || []).map(c => c.name.toLowerCase())
    )
  ).sort();

  const columns = [
    {
      title: "Foto",
      dataIndex: "photo",
      align: "center",
      render: (photo) => {
        if (!photo) {
          return <PictureOutlined className={styles.placeholderPhoto} />;
        }
        const content = (
          <div className={styles.photoWrapper}>
            <FallbackImage src={photo} alt="Producto" className={styles.hoverPhoto} fallback={FALLBACK_PHOTO} />
          </div>
        );
        return (
          <Popover content={content} trigger="hover" placement="right">
            <PictureOutlined className={styles.activePhotoIcon} />
          </Popover>
        );
      }
    },
    {
      title: "SKU",
      dataIndex: "sku",
      ...getColumnSearchProps("sku"),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Categoría",
      dataIndex: "id_category",
      align: "center",
      filters: categories,
      onFilter: (val, record) => record.id_category === val,
      render: (val) => getCategoryName(val),
    },
    {
      title: "Precio",
      dataIndex: "price",
      align: "center",
      render: (val) => `$${val.toFixed(2)}`,
    },
    {
      title: (
        <span className={styles.headerIgnoreRuleText}>
          Ignorar reglas de sucursales{" "}
          <Tooltip title="Al activar esta opción, el producto omitirá las configuraciones o bloqueos de canales (Uber, PedidosYa, Web, Kiosko, etc.) configurados específicamente en cada sucursal individual, aplicando únicamente la configuración global del canal.">
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </span>
      ),
      align: "center",
      dataIndex: "ignore_branch_overrides",
      render: (val, record) => (
        <Switch
          checkedChildren="Si"
          unCheckedChildren="No"
          checked={val === 1}
          onChange={() => handleToggleIgnoreOverrides(record.sku)}
        />
      ),
    },
    {
      title: "Acciones",
      align: "center",
      filterDropdown: (filterProps) => (
        <ConfigFilterDropdown
          {...filterProps}
          channels={uniqueChannels}
          showDeliveryMethods={false}
        />
      ),
      onFilter: (value, record) => {
        const parts = value.split(":");
        const status = parts[parts.length - 1];
        const channelName = parts.slice(1, parts.length - 1).join(":");
        const ch = record.channels?.find(c => c.name.toLowerCase() === channelName.toLowerCase());
        const isActive = ch ? (ch.active === true || ch.active === 1 || ch.active === '1') : false;
        return status === "active" ? isActive : !isActive;
      },
      render: (text, record) => {
        const platforms = record?.channels?.map(channel => ({
          key: channel.name,
          active: channel.active
        }));

        return (
          <Space direction="vertical" align="center" size="small">
            <Space size={4}>
              {platforms?.map(p => (
                <Tooltip key={p.key} title={`${p.key.toUpperCase()}: ${p.active ? 'Activo' : 'Inactivo'}`}>
                  <Badge color={p.active ? 'green' : 'red'} />
                </Tooltip>
              ))}
            </Space>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => handleOpenDrawer(record, "1")}
              className={styles.centerFlexBtn}
            >
              Gestionar
            </Button>
          </Space>
        );
      },
    },
  ];
  return (
    <div className={styles.view}>
      <Header title="Productos" />
      <div className={styles.buttons}>
        <Space className={styles.spacingBottom1em} align="start">
          <Button
            onClick={goToCondiments}
            type="primary"
            size="large"
            className={styles.mainButton}
          >
            CONDIMENTS
          </Button>
          <Button
            onClick={goToSetmenus}
            type="primary"
            size="large"
            className={styles.mainButton}
          >
            COMBOS
          </Button>
          <Button
            onClick={goToSuggets}
            type="primary"
            size="large"
            className={styles.mainButton}
          >
            RECOMENDADOS/SUGERIDOS
          </Button>
        </Space>
        {user.admin === 1 && (
          <div className={styles.actions}>
            <Button type="primary" size="large" className={styles.mainButton} onClick={gotoProdSuc}>VISTA SUCURSAL</Button>

            <div className={styles.syncContainer}>
              <Space align="center" >
                <Tooltip
                  title={isSyncing ? "Sincronizando..." : (lastSyncStatus ? (lastSyncStatus.startsWith("error") ? `Última sincronización falló: ${lastSyncStatus.replace("error: ", "")}` : "Sincronización completada exitosamente.") : "Sincronizar ahora")}
                >
                  <Button
                    type="primary"
                    size="large"
                    className={`${styles.mainButton} ${styles.syncButton}`}
                    style={{ width: "100%" }}
                    onClick={() => setSyncModalVisible(true)}
                    disabled={isSyncing}
                    icon={isSyncing ? null : (lastSyncStatus ? (lastSyncStatus.startsWith("error") ? <CloseCircleFilled style={{ color: '#ff4d4f', backgroundColor: '#ffffff', padding: '0.25rem', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} /> : <CheckCircleFilled style={{ color: '#52c41a', backgroundColor: '#ffffff', padding: '0.25rem', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />) : null)}
                  >
                    {isSyncing ? "Sincronizando..." : "SINCRONIZAR"}
                  </Button>
                </Tooltip>
                <Tooltip title="Recargar tabla">
                  <Button
                    type="default"
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={initialFetch}
                    loading={loading}
                    className={styles.reloadBtn}
                  />
                </Tooltip>
              </Space>
              {lastSyncTime && (
                <Text type="secondary" className={styles.syncTimeText}>
                  Última sincronización a las: {lastSyncTime}
                </Text>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        title="Opciones de Sincronización"
        visible={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        onOk={handleSyncSubmit}
        okText="Iniciar Sincronización"
        cancelText="Cancelar"
      >
        <div className={styles.syncOptionsContainer}>
          {/* Flujo 1: Sincronización General */}
          <div className={`${styles.syncSection} ${syncOptions.sku?.trim() ? styles.syncSectionDisabled : styles.syncSectionActive}`}>
            <span className={`${styles.syncSectionTitle} ${!syncOptions.sku?.trim() ? styles.syncSectionTitleActive : ''}`}>
              1. Sincronización General (por Entidades)
            </span>
            <div className={styles.syncOptionsContainer}>
              <Space align="center" className={styles.syncOptionsSpaceBetween}>
                <Text strong>Categorías</Text>
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  checked={syncOptions.sku?.trim() ? false : syncOptions.categories}
                  disabled={!!syncOptions.sku?.trim()}
                  onChange={(val) => setSyncOptions({ ...syncOptions, categories: val })}
                />
              </Space>
              <Space align="center" className={styles.syncOptionsSpaceBetween}>
                <Text strong>Productos</Text>
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  checked={syncOptions.sku?.trim() ? false : syncOptions.products}
                  disabled={!!syncOptions.sku?.trim()}
                  onChange={(val) => setSyncOptions({ ...syncOptions, products: val })}
                />
              </Space>
              <Space align="center" className={styles.syncOptionsSpaceBetween}>
                <Text strong>Set Menus</Text>
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  checked={syncOptions.sku?.trim() ? false : syncOptions.setmenu}
                  disabled={!!syncOptions.sku?.trim()}
                  onChange={(val) => setSyncOptions({ ...syncOptions, setmenu: val })}
                />
              </Space>
              <Space align="center" className={styles.syncOptionsSpaceBetween}>
                <Text strong>Condimentos</Text>
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  checked={syncOptions.sku?.trim() ? false : syncOptions.condiments}
                  disabled={!!syncOptions.sku?.trim()}
                  onChange={(val) => setSyncOptions({ ...syncOptions, condiments: val })}
                />
              </Space>
            </div>
          </div>

          <Divider plain className={styles.dividerMargin10_0}>
            <span className={styles.syncDividerText}>O BIEN</span>
          </Divider>

          {/* Flujo 2: SKU Específico */}
          <div className={`${styles.syncSection} ${syncOptions.sku?.trim() ? styles.syncSectionActive : ''}`}>
            <span className={`${styles.syncSectionTitle} ${syncOptions.sku?.trim() ? styles.syncSectionTitleActive : ''}`}>
              2. Sincronización por SKU Específico (Filtro Rápido)
            </span>
            <div>
              <Input
                placeholder="Ej: 000000000000002484, 000000000000002485"
                value={syncOptions.sku}
                onChange={(e) => setSyncOptions({ ...syncOptions, sku: e.target.value })}
                className={styles.syncSkuInput}
              />
              <Text type="secondary" className={styles.syncSkuHelpText}>
                Si ingresas uno o varios SKUs (separados por coma), solo se sincronizarán esos elementos (incluyendo todas las coincidencias del mismo SKU), ahorrando mucho tiempo.
              </Text>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        destroyOnClose
        wrapClassName={styles.modal}
        footer={false}
        visible={visibleEditor}
        width={300}
        closable={false}
        onCancel={closeEditor}
      >
        <PriceEditor closeEditor={closeEditor} />
      </Modal>

      <ProductDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedRecord(null);
        }}
        selectedRecord={selectedRecord}
        user={user}
        products={products}
        confirmVisibility={confirmVisibility}
        getProductMenu={getProductMenu}
        getProductSteps={getProductSteps}
        updateProductStepsOrder={updateProductStepsOrder}
      />

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        size="small"
      />
    </div>
  );
};

export default ProductsAdminUI;